// Parsing and validation of field values for every field type (PLAN §2, §4). Used by the API (claims),
// the importers and the UI (live interpretation before saving). Originals are always kept.

import { type FuzzyDate, parseFuzzyDate } from '../fuzzy-date';
import { foldText, matchEnumValue } from './match';
import type { ColumnType, EnumListDef, EnumValueDef, FieldDef, FieldType, TableColumn } from './types';

// ─── Money ───────────────────────────────────────────────────────────────────

export const AMOUNT_QUALIFIERS = ['exact', 'estimate', 'range', 'undisclosed'] as const;
export type AmountQualifier = (typeof AMOUNT_QUALIFIERS)[number];

export interface MoneyRange {
  min: number | null;
  max: number | null;
  /** ISO 4217 code, when stated. */
  currency: string | null;
  qualifier: AmountQualifier;
  /** "est.", "approx.", "~", "c." */
  approximate: boolean;
  period: 'year' | 'month' | null;
  /** Human-readable interpretation shown before saving. */
  label: string;
  /** Original text, kept verbatim. */
  raw: string;
}

const CURRENCY_SYMBOLS: [RegExp, string][] = [
  [/€|\beur\b|\beuros?\b/i, 'EUR'],
  [/£|\bgbp\b|\bpounds?\b/i, 'GBP'],
  [/₪|\bils\b|\bnis\b|\bshekels?\b/i, 'ILS'],
  [/\bchf\b|\bswiss francs?\b/i, 'CHF'],
  [/\bcad\b|\bc\$/i, 'CAD'],
  [/\baud\b|\ba\$/i, 'AUD'],
  [/\busd\b|\bus\$|\$|\bdollars?\b/i, 'USD'],
  [/¥|\bjpy\b|\byen\b/i, 'JPY'],
];
const SCALE: Record<string, number> = {
  k: 1e3,
  thousand: 1e3,
  m: 1e6,
  mn: 1e6,
  mio: 1e6,
  million: 1e6,
  millions: 1e6,
  b: 1e9,
  bn: 1e9,
  billion: 1e9,
  billions: 1e9,
  md: 1e9,
  mrd: 1e9,
  milliard: 1e9,
  milliards: 1e9,
};
const UNDISCLOSED = /\b(undisclosed|not disclosed|non divulgu|unknown|unspecified|confidential|n\/a|not public)/i;
const APPROX = /(^|[\s(])(~|≈|c\.|ca\.|circa|est\.?|estimated?|approx\.?|approximately|about|around|roughly|some)(?=[\s\d€$£₪]|$)/i;

/** "50,000" → 50000, "2,5" → 2.5, "1 000 000" → 1000000, "1.5" → 1.5. */
function parseNumber(s: string): number {
  const t = s.replace(/[\s\u00a0\u202f]/g, '');
  if (/^\d{1,3}([.,]\d{3})+$/.test(t)) return Number(t.replace(/[.,]/g, ''));
  if (/^\d{1,3}(,\d{3})+\.\d+$/.test(t)) return Number(t.replace(/,/g, ''));
  if (/^\d{1,3}(\.\d{3})+,\d+$/.test(t)) return Number(t.replace(/\./g, '').replace(',', '.'));
  return Number(t.replace(',', '.'));
}

const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

function moneyLabel(m: Omit<MoneyRange, 'label' | 'raw'>): string {
  if (m.qualifier === 'undisclosed') return 'Undisclosed amount';
  const cur = m.currency ? `${m.currency} ` : '';
  const amount =
    m.min !== null && m.max !== null && m.min !== m.max
      ? `${cur}${fmt(m.min)}–${fmt(m.max)}`
      : m.min !== null && m.max === null
        ? `at least ${cur}${fmt(m.min)}`
        : m.min === null && m.max !== null
          ? `up to ${cur}${fmt(m.max)}`
          : `${cur}${fmt(m.min ?? 0)}`;
  const per = m.period ? ` per ${m.period}` : '';
  const notes = [m.qualifier === 'range' ? 'range' : null, m.approximate ? 'estimate' : null, m.currency ? null : 'no currency'].filter(
    Boolean,
  );
  return `${amount}${per}${notes.length ? ` (${notes.join(', ')})` : ''}`;
}

/**
 * Parses what analysts write for amounts: "€50,000", "50 000 EUR", "€2–10M/year", "est. $1.5 million",
 * "over €1M", "up to 500k", "Primary funding — amount undisclosed". Returns null when there's no amount at all.
 */
export function parseMoneyRange(input: string): MoneyRange | null {
  const raw = input.trim();
  if (!raw) return null;
  const currency = CURRENCY_SYMBOLS.find(([re]) => re.test(raw))?.[1] ?? null;
  const approximate = APPROX.test(raw);
  const period: MoneyRange['period'] = /(\/\s*(year|yr|y|an|annum)\b|per (year|annum)|\bannual|\ba year\b|\bpar an\b|\/yr)/i.test(raw)
    ? 'year'
    : /(\/\s*(month|mo)\b|per month|\bmonthly|\ba month\b|par mois)/i.test(raw)
      ? 'month'
      : null;
  const re = /(\d[\d\s\u00a0\u202f.,]*\d|\d)\s*(k|thousand|mn|mio|millions?|m|bn|billions?|b|md|mrd|milliards?)?(?![\p{L}])/giu;
  const nums: { n: number; base: number; scale: number; scaled: boolean; idx: number; end: number }[] = [];
  for (const m of raw.matchAll(re)) {
    // A year next to an amount ("since 2021", "2019–2024") is a period, not money.
    const bare = !m[2] && /^(19|20)\d\d$/.test(m[1]?.trim() ?? '');
    if (bare) continue;
    const scale = m[2] ? (SCALE[m[2].toLowerCase()] ?? 1) : 1;
    const base = parseNumber(m[1] ?? '');
    nums.push({ n: base * scale, base, scale, scaled: !!m[2], idx: m.index ?? 0, end: (m.index ?? 0) + m[0].length });
  }
  const valid = nums.filter(x => Number.isFinite(x.n));
  if (!valid.length) {
    if (UNDISCLOSED.test(raw)) {
      const base = { min: null, max: null, currency, qualifier: 'undisclosed' as const, approximate: false, period };
      return { ...base, label: moneyLabel(base), raw };
    }
    return null;
  }
  let [a, b] = valid;
  if (a && b) {
    const between = raw.slice(a.end, b.idx);
    // "2-10M": the scale written once applies to both ends.
    if (!/^\s*([-–—]|to|à|and|et)\s*[€$£₪]?\s*$/i.test(between)) b = undefined;
    else if (!a.scaled && b.scaled) a = { ...a, n: a.base * b.scale };
  }
  const lower = raw.toLowerCase();
  let min: number | null = a?.n ?? null;
  let max: number | null = b ? b.n : min;
  if (!b && /\b(over|more than|above|at least|minimum|min\.|plus de|au moins)\b|\+\s*$/.test(lower)) max = null;
  if (!b && /\b(up to|under|less than|below|at most|maximum|max\.|jusqu)/.test(lower)) {
    max = min;
    min = null;
  }
  if (min !== null && max !== null && min > max) [min, max] = [max, min];
  const qualifier: AmountQualifier = min !== max ? 'range' : approximate ? 'estimate' : 'exact';
  const base = { min, max, currency, qualifier, approximate, period };
  return { ...base, label: moneyLabel(base), raw };
}

// ─── Ratings ─────────────────────────────────────────────────────────────────

export interface RatingValue {
  /** Dimension name as written in the sheet ("Media Influence"), for multi-valued ratings. */
  dimension?: string | null;
  /** Canonical dimension key from the `rating_dimension` list, when mapped. */
  canonicalDimension?: string | null;
  /** Normalized 1–5 (null = not rated / not understood). */
  value: number | null;
  /** Original wording ("Low-Medium", "High Risk", "3/5"). */
  original: string;
  /** Qualifier kept from the original ("Risk" in "High Risk"). */
  note?: string | null;
  justification: string;
}

/**
 * Normalizes a rating to 1–5 with the original kept: scale words and their synonyms from the rating-scale
 * list ("Low", "Low-Medium", "High Risk"), or numbers ("3", "4/5", "7/10").
 */
export function normalizeRating(
  input: string,
  scale: readonly Pick<EnumValueDef, 'key' | 'label' | 'synonyms' | 'score'>[],
): { value: number | null; note: string | null } {
  const raw = input.trim();
  const num = raw.match(/^(\d+(?:[.,]\d+)?)\s*(?:\/\s*(\d+))?(?:\s|$)/);
  if (num) {
    const v = Number(num[1]?.replace(',', '.'));
    const outOf = num[2] ? Number(num[2]) : 5;
    if (outOf > 0 && v >= 0 && v <= outOf) {
      const rest = raw.slice(num[0].length).trim();
      return { value: Math.min(5, Math.max(1, Math.round((v / outOf) * 5 * 10) / 10 || 1)), note: rest || null };
    }
  }
  const m = matchEnumValue(scale, raw);
  return m.value && m.value.score !== null
    ? { value: m.value.score, note: m.how === 'qualified' ? m.note : null }
    : { value: null, note: null };
}

// ─── Social handles ──────────────────────────────────────────────────────────

export const SOCIAL_PLATFORMS = {
  x: { label: 'X (Twitter)', hosts: ['x.com', 'twitter.com'], names: ['x', 'twitter', 'tw'] },
  facebook: { label: 'Facebook', hosts: ['facebook.com', 'fb.com'], names: ['facebook', 'fb'] },
  instagram: { label: 'Instagram', hosts: ['instagram.com'], names: ['instagram', 'insta', 'ig'] },
  youtube: { label: 'YouTube', hosts: ['youtube.com', 'youtu.be'], names: ['youtube', 'yt'] },
  linkedin: { label: 'LinkedIn', hosts: ['linkedin.com'], names: ['linkedin'] },
  tiktok: { label: 'TikTok', hosts: ['tiktok.com'], names: ['tiktok'] },
  telegram: { label: 'Telegram', hosts: ['t.me', 'telegram.me'], names: ['telegram'] },
  bluesky: { label: 'Bluesky', hosts: ['bsky.app'], names: ['bluesky', 'bsky'] },
  threads: { label: 'Threads', hosts: ['threads.net'], names: ['threads'] },
  mastodon: { label: 'Mastodon', hosts: [], names: ['mastodon'] },
  other: { label: 'Other', hosts: [], names: [] },
} as const;
export type SocialPlatform = keyof typeof SOCIAL_PLATFORMS;

export interface SocialHandle {
  platform: SocialPlatform;
  /** Without the leading @. */
  handle: string;
  url: string | null;
}

function platformFromHost(host: string): SocialPlatform {
  const h = host.replace(/^www\./, '').replace(/^m\./, '');
  for (const [k, p] of Object.entries(SOCIAL_PLATFORMS))
    if ((p.hosts as readonly string[]).some(x => h === x || h.endsWith(`.${x}`))) return k as SocialPlatform;
  return 'other';
}

/** "Twitter/X", "Facebook (page)", "LinkedIn" → the platform; "other" when no word names one. */
function platformFromLabel(label: string): SocialPlatform {
  const words = foldText(label)
    .replace(/\(.*$/, '')
    .split(/[\s/]+/)
    .filter(Boolean);
  for (const w of words)
    for (const [k, p] of Object.entries(SOCIAL_PLATFORMS)) if ((p.names as readonly string[]).includes(w)) return k as SocialPlatform;
  return 'other';
}

const SOCIAL_PARTS = /\s*(?:\||;|\n|,\s(?=[\p{L} ()/]+\s*:)|\s[—–]\s)\s*/u;
const EMAIL_ONLY = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const WEB_ONLY = /^(?:https?:\/\/\S+|www\.\S+|[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\/\S*)?)$/i;
/** A bare domain ("change.org") can be an organization's name; only a scheme, "www." or a path makes it an address. */
const BARE_DOMAIN = /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}$/i;

/**
 * What a piece of text is when it is nothing but contact addresses: a website ("https://www.ec4i.org", "ec4i.org"),
 * social media accounts ("Facebook: ec4i.org | Twitter/X: @EC4Israel", "@EC4Israel", a profile link) or an email
 * address. Null for anything else, a name included. Extraction uses it so an address never becomes an actor. A bare
 * domain counts only with `bareDomain` (where the wording already says it is a website): "Change.org" is an NGO.
 */
export function addressKind(text: string, opts: { bareDomain?: boolean } = {}): 'website' | 'social' | 'email' | null {
  const t = text.trim();
  if (!t || t.length > 500) return null;
  if (EMAIL_ONLY.test(t)) return 'email';
  let social = false;
  for (const part of t.split(SOCIAL_PARTS)) {
    const p = part.trim();
    if (!p) continue;
    // @name, or a federated @name@server (Mastodon and similar).
    if (/^@[\w.]{2,}(?:@[\w-]+(?:\.[\w-]+)+)?$/.test(p)) {
      social = true;
      continue;
    }
    if (WEB_ONLY.test(p)) {
      if (!/^www\./i.test(p) && BARE_DOMAIN.test(p) && !opts.bareDomain) return null;
      try {
        if (platformFromHost(new URL(/^https?:/i.test(p) ? p : `https://${p}`).hostname) !== 'other') social = true;
      } catch {
        return null;
      }
      continue;
    }
    const labelled = p.match(/^([\p{L} ()/]+?)\s*[:：]\s*(\S+)$/u);
    if (labelled && platformFromLabel(labelled[1] ?? '') !== 'other') {
      social = true;
      continue;
    }
    return null;
  }
  return social ? 'social' : 'website';
}

/** "X: @FrancTireurMag | Facebook: franctireurmag | https://instagram.com/franc.tireur.mag" → handles. */
export function parseSocialHandles(input: string): SocialHandle[] {
  const out: SocialHandle[] = [];
  for (const part of input.split(SOCIAL_PARTS)) {
    const p = part.trim();
    if (!p) continue;
    const url = p.match(/https?:\/\/[^\s)]+|(?:www\.)?[a-z0-9-]+\.(?:com|net|app|me|be)\/[^\s)]+/i)?.[0];
    if (url) {
      const u = new URL(url.startsWith('http') ? url : `https://${url}`);
      const platform = platformFromHost(u.hostname);
      const handle = decodeURIComponent(
        u.pathname
          .split('/')
          .filter(Boolean)
          .filter(s => !['channel', 'c', 'user', 'in', 'company'].includes(s))[0] ?? u.hostname,
      ).replace(/^@/, '');
      out.push({ platform, handle, url: u.toString() });
      continue;
    }
    const labelled = p.match(/^([\p{L} ()/]+?)\s*[:：]\s*(.+)$/u);
    const platform = labelled ? platformFromLabel(labelled[1] ?? '') : 'other';
    const handle = (labelled ? (labelled[2] ?? '') : p).trim().replace(/^@/, '');
    if (handle) out.push({ platform, handle, url: null });
  }
  return out;
}

// ─── References ──────────────────────────────────────────────────────────────

/** A pointer at an entity; `text` alone = not yet resolved (the importer and review link it later). */
export interface RefValue {
  id?: string | null;
  text: string;
}

// ─── Validation ──────────────────────────────────────────────────────────────

export type FieldValue = unknown;
export type ValidationResult = { ok: true; value: FieldValue } | { ok: false; errors: string[] };

export interface ValidationContext {
  lists: readonly EnumListDef[];
}

type Validatable = Pick<FieldDef, 'label' | 'dataType' | 'multiValued' | 'required'> &
  Partial<Pick<FieldDef, 'enumList' | 'ratingScale' | 'justificationRequired' | 'columns'>>;

const isBlank = (v: unknown) =>
  v === null || v === undefined || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[\d\s().\-/]{6,24}$/;

function listValues(ctx: ValidationContext, key: string | null | undefined) {
  return ctx.lists.find(l => l.key === key)?.values ?? [];
}

function one(type: FieldType | ColumnType, v: unknown, def: Validatable, ctx: ValidationContext): ValidationResult {
  const fail = (e: string): ValidationResult => ({ ok: false, errors: [`${def.label}: ${e}`] });
  switch (type) {
    case 'text':
    case 'longtext':
      return typeof v === 'string' ? { ok: true, value: v.trim() } : fail('expected text.');
    case 'number': {
      const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(/[\s,]/g, '')) : Number.NaN;
      return Number.isFinite(n) ? { ok: true, value: n } : fail('expected a number.');
    }
    case 'fuzzy_date': {
      if (typeof v === 'object' && v && 'raw' in v && 'label' in v) return { ok: true, value: v as FuzzyDate };
      const d = typeof v === 'string' ? parseFuzzyDate(v) : null;
      return d ? { ok: true, value: d } : fail('date not recognised. Try 2019-03, "March 2019", "c. 2021" or "2017–2024".');
    }
    case 'enum': {
      const values = listValues(ctx, def.enumList);
      if (typeof v !== 'string') return fail('expected a value from the list.');
      const exact = values.find(x => x.key === v && !x.archived);
      if (exact) return { ok: true, value: exact.key };
      const m = matchEnumValue(values, v);
      return m.value && m.how !== 'qualified'
        ? { ok: true, value: m.value.key }
        : fail(`"${v}" is not in the list. Pick a value${m.value ? ` (closest: ${m.value.label})` : ''}, or ask an admin to add it.`);
    }
    case 'url': {
      if (typeof v !== 'string') return fail('expected a web address.');
      const s = v.trim();
      const withScheme = /^https?:\/\//i.test(s) ? s : /^[\w-]+(\.[\w-]+)+(\/|$)/.test(s) ? `https://${s}` : null;
      try {
        return withScheme ? { ok: true, value: new URL(withScheme).toString() } : fail('expected a web address starting with https://.');
      } catch {
        return fail('expected a web address starting with https://.');
      }
    }
    case 'email':
      return typeof v === 'string' && EMAIL.test(v.trim()) ? { ok: true, value: v.trim() } : fail('expected an email address.');
    case 'phone':
      return typeof v === 'string' && PHONE.test(v.trim()) ? { ok: true, value: v.trim() } : fail('expected a phone number.');
    case 'money_range': {
      if (typeof v === 'object' && v && 'qualifier' in v && 'raw' in v) return { ok: true, value: v };
      const m = typeof v === 'string' ? parseMoneyRange(v) : null;
      return m ? { ok: true, value: m } : fail('amount not recognised. Try "€50,000", "€2–10M", "est. $1M" or "undisclosed".');
    }
    case 'rating': {
      const r = (typeof v === 'string' ? { original: v, justification: '' } : v) as Partial<RatingValue> | null;
      if (!r || typeof r !== 'object') return fail('expected a rating.');
      const original = String(r.original ?? r.value ?? '').trim();
      const scale = listValues(ctx, def.ratingScale);
      const norm = typeof r.value === 'number' && !r.original ? { value: r.value, note: null } : normalizeRating(original, scale);
      if (norm.value === null) return fail(`"${original}" is not on the rating scale (${scale.map(s => s.label).join(', ') || '1–5'}).`);
      const justification = String(r.justification ?? '').trim();
      if (def.justificationRequired && !justification) return fail('a justification is required for every rating.');
      const dimension = r.dimension?.trim() || null;
      const canonical =
        r.canonicalDimension ?? (dimension ? (matchEnumValue(listValues(ctx, 'rating_dimension'), dimension).value?.key ?? null) : null);
      const value: RatingValue = {
        dimension,
        canonicalDimension: canonical,
        value: norm.value,
        original: original || String(norm.value),
        note: norm.note,
        justification,
      };
      return { ok: true, value };
    }
    case 'social_handles': {
      const list = typeof v === 'string' ? parseSocialHandles(v) : Array.isArray(v) ? (v as SocialHandle[]) : [];
      const clean = list.filter(h => h && typeof h.handle === 'string' && h.handle.trim());
      return clean.length ? { ok: true, value: clean } : fail('no handle recognised. Try "X: @name | Facebook: page".');
    }
    case 'entity_ref':
    case 'place_ref':
    case 'event_ref': {
      const r = (typeof v === 'string' ? { text: v } : v) as RefValue | null;
      return r && typeof r === 'object' && (r.id || r.text?.trim())
        ? { ok: true, value: { id: r.id ?? null, text: (r.text ?? '').trim() } }
        : fail('expected a name or a link to an entity.');
    }
    case 'multi_enum': {
      const arr = Array.isArray(v) ? v : typeof v === 'string' ? v.split(/\s*[,;|+]\s*/) : [];
      const res = arr.filter(x => !isBlank(x)).map(x => one('enum', x, def, ctx));
      const errors = res.flatMap(r => (r.ok ? [] : r.errors));
      return errors.length ? { ok: false, errors } : { ok: true, value: [...new Set(res.map(r => (r.ok ? r.value : null)))] };
    }
    case 'table':
      return validateTable(v, def, ctx);
  }
}

function validateTable(v: unknown, def: Validatable, ctx: ValidationContext): ValidationResult {
  const columns: TableColumn[] = def.columns ?? [];
  if (!Array.isArray(v)) return { ok: false, errors: [`${def.label}: expected table rows.`] };
  const rows: Record<string, unknown>[] = [];
  const errors: string[] = [];
  for (const [i, row] of v.entries()) {
    if (!row || typeof row !== 'object') {
      errors.push(`${def.label}, row ${i + 1}: expected a row.`);
      continue;
    }
    const out: Record<string, unknown> = {};
    for (const col of columns) {
      const cell = (row as Record<string, unknown>)[col.key];
      const colDef: Validatable = {
        label: `${def.label}, row ${i + 1}, ${col.label}`,
        dataType: col.type === 'event_ref' ? 'entity_ref' : col.type,
        multiValued: !!col.multiValued,
        required: !!col.required,
        enumList: col.enumList ?? null,
        ratingScale: def.ratingScale ?? 'rating_scale',
        justificationRequired: false,
      };
      if (isBlank(cell)) {
        if (col.required) errors.push(`${colDef.label}: required.`);
        continue;
      }
      const r = col.multiValued && col.type !== 'multi_enum' ? many(col.type, cell, colDef, ctx) : one(col.type, cell, colDef, ctx);
      if (r.ok) out[col.key] = r.value;
      else errors.push(...r.errors);
    }
    // Rows keep cells for columns an admin removed later, so nothing written is ever lost.
    for (const [k, cell] of Object.entries(row as Record<string, unknown>))
      if (!(k in out) && !columns.some(c => c.key === k)) out[k] = cell;
    if (Object.keys(out).length) rows.push(out);
  }
  return errors.length ? { ok: false, errors } : { ok: true, value: rows };
}

function many(type: FieldType | ColumnType, v: unknown, def: Validatable, ctx: ValidationContext): ValidationResult {
  const arr = Array.isArray(v) ? v : [v];
  const res = arr.filter(x => !isBlank(x)).map(x => one(type, x, def, ctx));
  const errors = res.flatMap(r => (r.ok ? [] : r.errors));
  return errors.length ? { ok: false, errors } : { ok: true, value: res.map(r => (r.ok ? r.value : null)) };
}

/**
 * Validates and normalizes a value for a field: parses free text (dates, amounts, ratings, handles),
 * maps list values case-insensitively, enforces required fields and rating justifications.
 */
export function validateFieldValue(def: Validatable, value: unknown, ctx: ValidationContext): ValidationResult {
  if (isBlank(value)) return def.required ? { ok: false, errors: [`${def.label}: required.`] } : { ok: true, value: null };
  if (def.multiValued && def.dataType !== 'multi_enum' && def.dataType !== 'table' && def.dataType !== 'social_handles')
    return many(def.dataType, value, def, ctx);
  return one(def.dataType, value, def, ctx);
}

/** Short human-readable rendering of a normalized value (lists, previews, exports). */
export function formatFieldValue(
  def: Pick<FieldDef, 'dataType'> & Partial<Pick<FieldDef, 'enumList'>>,
  v: unknown,
  ctx?: ValidationContext,
): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v) && def.dataType !== 'table' && def.dataType !== 'social_handles' && def.dataType !== 'multi_enum')
    return v.map(x => formatFieldValue(def, x, ctx)).join('; ');
  const label = (k: unknown) => ctx?.lists.find(l => l.key === def.enumList)?.values.find(x => x.key === k)?.label ?? String(k);
  switch (def.dataType) {
    case 'fuzzy_date':
      return (v as FuzzyDate).label;
    case 'money_range':
      return (v as MoneyRange).label;
    case 'rating': {
      const r = v as RatingValue;
      return `${r.dimension ? `${r.dimension}: ` : ''}${r.original} (${r.value}/5)`;
    }
    case 'social_handles':
      return (v as SocialHandle[]).map(h => `${SOCIAL_PLATFORMS[h.platform]?.label ?? h.platform}: ${h.handle}`).join(' · ');
    case 'enum':
      return label(v);
    case 'multi_enum':
      return (v as unknown[]).map(label).join(', ');
    case 'entity_ref':
    case 'place_ref':
      return (v as RefValue).text;
    case 'table':
      return `${(v as unknown[]).length} row${(v as unknown[]).length === 1 ? '' : 's'}`;
    default:
      return String(v);
  }
}
